import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import { AnimatePresence, motion } from "framer-motion";
import { isObject, isString, difference, orderBy } from "lodash-es";

import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import useMainFilter from "../../hooks/useMainFilter";
import { useNavigate, useParams } from "react-router-dom";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import PageTitle from "../../components/PageTitle/PageTitle";

import {
  MdArrowDownward,
  MdArrowRight,
  MdArrowUpward,
  MdCheck,
  MdConnectWithoutContact,
  MdDelete,
  MdFileUpload,
  MdMoreHoriz,
  MdOutlineAccountBalance,
  MdOutlineArchive,
  MdOutlineAutoDelete,
  MdOutlineContentCopy,
  MdOutlineCreditCard,
  MdOutlineCreditScore,
  MdOutlineFolder,
  MdOutlineRepeat,
  MdOutlineSwapVert,
  MdOutlineUnarchive,
  MdRestoreFromTrash,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import {
  FILE_STORAGE_SERVER_URL,
  ICON_SIZES,
  TRANSACTION_TYPES,
  ETransactionType,
  BUTTON_STYLES,
} from "../../helpers/constants";
import {
  formatCurrency,
  formatFileSize,
  getTotalByTransactionType,
  objectHasChanges,
  showNotification,
  transformToSelectOptions,
} from "../../helpers/utils";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import useMobile from "../../hooks/useMobile";
import useAccountsDropdownMenu from "../../hooks/useAccountsDropdownMenu";
import TodayTotalsBar from "../../components/TodayTotalsBar/TodayTotalsBar";
import CurrentAccountCard from "../../components/CurrentAccountCard/CurrentAccountCard";
import LinksList from "../../components/LinksList/LinksList";
import React, { useEffect, useMemo, useRef, useState } from "react";
import AccountNotFound from "../../components/AccountNotFound/AccountNotFound";
import TransactionHeader from "./TransactionHeader";
import TransactionDetails, {
  TransactionDetailRowsType,
} from "./TransactionDetails";
import TransactionNotes from "./TransactionNotes";
import TransactionTotals from "./TransactionTotals";
import FieldsValidation from "../../components/FieldsValidation/FieldsValidation";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import {
  addTransaction,
  cloneTransaction,
  deleteTransactions,
  updateTransaction as updateTransactionData,
  updateTransactionsText,
} from "../../api/transactions";
import useCache from "../../hooks/useCache";
import moment from "moment";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import TransactionPayment from "./TransactionPayment";
import CollapsibleBlock from "../../components/CollapsibleBlock/CollapsibleBlock";
import { TRANSACTION_STATUS_PAID } from "../../constants/transaction-status";
import useCollapsibles from "../../hooks/useCollapsibles";
import { IFilePreview } from "../../components/AttachmentsSelector/AttachmentsSelector";
import useCurrentUser from "../../hooks/useCurrentUser";
import TransactionsNavigation from "../../components/TransactionsNavigation/TransactionsNavigation";
import useAccountLocalStorage from "../../hooks/useAccountLocalStorage";
import TransactionAttachments from "./TransactionAttachments";
import useDarkMode from "src/hooks/useDarkMode";
import useInvoiceParser from "src/hooks/useInvoiceParser";
import TransactionAttachmentsLightbox from "./TransactionAttachmentsLightbox";
import ScrollToSection from "src/components/ScrollToSection/ScrollToSection";
import RecurringTransactionSettings from "src/components/RecurringTransaction/RecurringTransactionSettings";
import RecurringTransactionInfo from "src/components/RecurringTransaction/RecurringTransactionInfo";
import RecurringTransactionPayment from "src/components/RecurringTransaction/RecurringTransactionPayment";
import RecurringTransactionOrigin from "src/components/RecurringTransaction/RecurringTransactionOrigin";
import TransactionClientProviderInfo from "./TransactionClientProviderInfo";
import useAllTransactions from "src/hooks/useAllTransactions";
import useFilteredTransactions from "src/hooks/useFilteredTransactions";
import SlideDownUpAnimation from "src/components/Animations/SlideDownUpAnimation";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import ShareTransactionModal from "src/components/ShareTransactionModal/ShareTransactionModal";
import DuplicateTransactionsDialog from "src/components/DuplicateTransactionsDialog/DuplicateTransactionsDialog";
import SelectDialog from "src/components/SelectDialog/SelectDialog";
import { ICustomSelectOption } from "src/components/Select/CustomSelect";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import PageSubtitleAccount from "src/components/PageTitle/PageSubtitleAccount";
import MarqueeRow from "src/components/MarqueeRow/MarqueeRow";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";

const NewTransactionPage: React.FC = () => {
  const downloadRef = useRef<any>(null);
  const thumbnailsRef = useRef<any>(null);
  const [nextTransactionInNavigation, setNextTransactionInNavigation] =
    useState<any>("");
  const [prevTransactionInNavigation, setPrevTransactionInNavigation] =
    useState<any>("");

  const { isFilteringTransactions } = useFilteredTransactions();

  const [showRecurringPaymentModal, setShowRecurringPaymentModal] =
    useState<boolean>(false);
  const {
    isLoadingCustomizedSources,
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isRefetchingAllTransactions,
    getTransactionById,
  } = useAllTransactions();
  const [showRecurringSettings, setShowRecurringSettings] =
    useState<boolean>(false);
  const [canScroll, setCanScroll] = useState(false);

  const { accountLocalStoragePreferences, updateAccountLocalStorage } =
    useAccountLocalStorage();
  const [attachmentsKey, setAttachmentsKey] = useState<number>(0);

  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);
  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);
  const { isEnabled: isDarkMode } = useDarkMode();

  const { account, transaction } = useParams();
  const [isLoadingExistingTransaction, setIsLoadingExistingTransaction] =
    useState<boolean>(!!account && !!transaction);
  const { isLoadingAccounts, getPaymentMethodById, allUserAccounts } =
    useCurrentUser();
  const [attachments, setAttachments] = useState<Array<any>>([]);
  const [unsavedAttachments, setUnsavedAttachments] = useState<IFilePreview[]>(
    []
  );
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<any[]>([]);
  const [recurringSettings, setRecurringSettings] = useState<any>({
    settings: {
      frequency: "",
      customInterval: 0,
      createdAt: "",
      disabledAt: "",
    },
    enabled: false,
    nextPayment: "",
  });

  const { updateCollapsible, collapsiblePreferences } = useCollapsibles();
  const { mainFilter, setMainFilter } = useMainFilter();
  const {
    currentAccountData,
    isLoadingCurrentAccount,
    activeTransactions,
    currencySymbol,
  } = useCurrentAccount();
  const { clearCache } = useCache();
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const accountsGlobalMenu = useAccountsDropdownMenu({
    linkTo: "transaction",
  });
  const [activeTab, setActiveTab] = useState<ETransactionType>(
    accountLocalStoragePreferences.lastTransactionType
  );
  const [loadedTransaction, setLoadedTransaction] = useState<any>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setMainFilter({ account });
  }, [account]);

  const {
    onDragLeave,
    onDragOver,
    draggingStyles,
    onDrop,
    fileInputRef,
    triggerFileSelect,
    onFileChange,
  } = useInvoiceParser({
    onParse: (data: any) => {
      navigate(`/transaction/${mainFilter?.account}`);
      setAttachments([]);
      setUnsavedAttachments([]);
      setClientProvider(
        data?.parsed?.encabezado?.nombreComercial ||
          data?.parsed?.encabezado?.origen
      );
      setDate(moment(data?.parsed?.encabezado?.fecha).format("yyyy-MM-DD"));
      setTaxes(data?.parsed?.montos?.impuestos);
      setDiscount(data?.parsed?.montos?.descuento);
      setNotes(data?.parsed?.notas);

      setDocumentNumber(data?.parsed?.encabezado?.numeroDeDocumento);

      const parsedDetails = data?.parsed?.detalles?.map?.((row: any) => ({
        quantity: row?.cantidad,
        description: row?.detalle,
        unitPrice: row?.precio,
      }));

      setDescription(
        parsedDetails?.length ? "" : data?.parsed?.encabezado?.descripcion
      );

      setDetails(parsedDetails);
    },
  });

  const [isArchivingTransactions, setIsArchivingTransactions] =
    useState<boolean>(false);
  const [showArchiveTransactionsDialog, setShowArchiveTransactionsDialog] =
    useState<boolean>(false);
  const [showUnarchiveTransactionsDialog, setShowUnarchiveTransactionsDialog] =
    useState<boolean>(false);
  const [isUnarchivingTransactions, setIsUnarchivingTransactions] =
    useState<boolean>(false);

  const [showDeleteTransactionsDialog, setShowDeleteTransactionsDialog] =
    useState<boolean>(false);
  const [isDeletingTransactions, setIsDeletingTransactions] =
    useState<boolean>(false);

  const [isRecyclingTransactions, setIsRecyclingTransactions] =
    useState<boolean>(false);
  const [showRecycleTransactionsDialog, setShowRecycleTransactionsDialog] =
    useState<boolean>(false);
  const [isRestoringTransactions, setIsRestoringTransactions] =
    useState<boolean>(false);

  const [showRestoreTransactionsDialog, setShowRestoreTransactionsDialog] =
    useState<boolean>(false);
  const [isSharingTransactions, setIsSharingTransactions] =
    useState<boolean>(false);
  const [showShareTransactionsDialog, setShowShareTransactionsDialog] =
    useState<boolean>(false);
  const [showDuplicateTransactionDialog, setShowDuplicateTransactionDialog] =
    useState<boolean>(false);
  const [isDuplicatingTransactions, setIsDuplicatingTransactions] =
    useState<boolean>(false);

  const [showMoveTransactionsDialog, setShowMoveTransactionsDialog] =
    useState<boolean>(false);
  const [isMovingTransactions, setIsMovingTransactions] =
    useState<boolean>(false);

  const [isNewTransaction, setIsNewTransaction] = useState<boolean>(true);
  const [clientProvider, setClientProvider] = useState<string>("");
  const [date, setDate] = useState<string>(moment().format("yyyy-MM-DD"));
  const [documentNumber, setDocumentNumber] = useState<string>("");
  const [details, setDetails] = useState<TransactionDetailRowsType>([]);
  const [description, setDescription] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [taxes, setTaxes] = useState<string>("");
  const [discount, setDiscount] = useState<string>("");
  const [manualSubTotal, setManualSubTotal] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>(
    accountLocalStoragePreferences.lastPaymentMethod
  );
  const [status, setStatus] = useState<string>(TRANSACTION_STATUS_PAID);
  const [receiptNumber, setReceiptNumber] = useState<string>("");
  const [archived, setArchived] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [received, setReceived] = useState<boolean>(false);
  const [receivedMetaData, setReceivedMetaData] = useState<object | null>(null);
  const [tags, setTags] = useState<Array<string>>([]);

  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [isSavingTransaction, setIsSavingTransaction] =
    useState<boolean>(false);

  const [hasRequiredFieldsCompleted, setHasRequiredFieldsCompleted] =
    useState<boolean>(true);

  const subTotal = useMemo(() => {
    if (details?.length) {
      let total = 0;

      details?.forEach?.(({ quantity, unitPrice }) => {
        total += Number(unitPrice) * Number(quantity);
      });

      return `${total}`;
    }

    return `${manualSubTotal}`;
  }, [details, manualSubTotal]);

  const getAvailableMoney = useMemo(() => {
    const income = getTotalByTransactionType({
      type: ETransactionType.INCOME,
      transactions: activeTransactions,
    });
    const expenses = getTotalByTransactionType({
      type: ETransactionType.EXPENSE,
      transactions: activeTransactions,
    });

    return income - expenses;
  }, [activeTransactions]);

  const total = useMemo(() => {
    return `${Number(taxes) + Number(subTotal) - Number(discount)}`;
  }, [taxes, subTotal, discount]);

  const saveTransaction = async () => {
    const newTransactionId = uuidv4();

    setIsSavingTransaction(true);
    try {
      const data = await addTransaction({
        transactionId: newTransactionId,
        fieldsToSave: {
          invoice: newTransactionId,
          date,
          documentNumber,
          payTo: `${clientProvider}`?.trim?.(),
          notes,
          taxes,
          account: mainFilter?.account,
          details: details?.length ? details : [],
          description: `${description}`.trim() || "",
          subTotal,
          type: activeTab,
          discount,
          category: `${category}`.trim() || "",
          status,
          paymentMethod,
          total,
          tags: [],
          paymentVoucher: receiptNumber,
        },
        files: unsavedAttachments,
      });

      if (data?.success) {
        setUnsavedAttachments([]);
        setAttachmentsToDelete([]);
        setAttachmentsKey((curr: any) => curr + 1);

        await clearCache();

        setShowSaveDialog(false);
        showNotification({
          message: "Transacción agregada",
          kind: "success",
        });
        navigate(`/transaction/${data?.data?.account}/${data?.data?.invoice}`, {
          replace: true,
        });
      } else {
        showNotification({
          message: "Ocurrió un error guardando la transacción",
          kind: "error",
        });
      }
    } catch (e) {
      showNotification({
        message: "Ocurrió un error guardando la transacción",
        kind: "error",
      });
    } finally {
      setIsSavingTransaction(false);
    }
  };

  const attachmentsForSelector = useMemo(() => {
    return difference(attachments, attachmentsToDelete);
  }, [attachmentsToDelete, attachments]);

  const formattedAttachmentsPerVersion = useMemo(() => {
    if (attachments?.length) {
      const [firstAttachment] = attachments;

      if (isString(firstAttachment)) {
        return attachments.map?.((simpleStringAttachment) => ({
          fileUrl: `${FILE_STORAGE_SERVER_URL}/${simpleStringAttachment}`,
          originalName: simpleStringAttachment,
          size: "N/A",
          storedFileName: simpleStringAttachment,
          thumbnailFileName: simpleStringAttachment,
          thumbnailUrl: `${FILE_STORAGE_SERVER_URL}/${simpleStringAttachment}`,
          type: "N/A",
          uploaderVersion: "v1",
        }));
      } else if (isObject(firstAttachment)) {
        return attachmentsForSelector;
      }
    }
    return [];
  }, [attachments]);

  const navigateToNexOrPrevTransaction = () => {
    if (nextTransactionInNavigation) {
      navigate(
        `/transaction/${loadedTransaction?.account}/${nextTransactionInNavigation}`,
        {
          replace: true,
        }
      );
    } else if (prevTransactionInNavigation) {
      navigate(
        `/transaction/${loadedTransaction?.account}/${prevTransactionInNavigation}`,
        {
          replace: true,
        }
      );
    }
  };

  const updateTransaction = async () => {
    setIsSavingTransaction(true);
    try {
      const result = await updateTransactionData({
        transactionId: transaction || "",
        fieldsToUpdate: {
          date,
          documentNumber,
          payTo: `${clientProvider}`?.trim?.(),
          notes,
          taxes,
          account,
          details: details?.length ? details : [],
          attachments: formattedAttachmentsPerVersion,
          description: `${description}`.trim() || "",
          subTotal,
          type: activeTab,
          discount,
          category: `${category}`.trim() || "",
          status,
          paymentMethod,
          total,
          tags,
          archived,
          deleted,
          sharedBy: receivedMetaData,
          shared: received,
          paymentVoucher: receiptNumber,
        },
        files: unsavedAttachments,
      });

      if (result?.success) {
        setUnsavedAttachments([]);
        setAttachmentsToDelete([]);
        setAttachments(result?.data?.attachments || []);
        setAttachmentsKey((curr: any) => curr + 1);
        setLoadedTransaction(result?.data);

        await clearCache();

        setShowSaveDialog(false);
        setIsSavingTransaction(false);

        showNotification({
          message: "Cambios guardados",
          kind: "success",
        });
      } else {
        setIsSavingTransaction(false);
        showNotification({
          message: "Error guardando los cambios",
          kind: "error",
        });
      }
    } catch (e) {
      setIsSavingTransaction(false);
      showNotification({
        message: "Error guardando los cambios",
        kind: "error",
      });
    }
  };

  const loadTransaction = () => {
    setIsNewTransaction(false);
    setHasRequiredFieldsCompleted(true);

    if (
      account &&
      transaction &&
      !isLoadingCurrentAccount &&
      !isLoadingAccounts &&
      !isLoadingAllTransactions &&
      !isLoadingCustomizedCategories &&
      !isLoadingCustomizedSources
    ) {
      setIsLoadingExistingTransaction(true);
      const data =
        getTransactionById({ transactionId: transaction || "" }) || null;

      if (!data) {
        navigate("/404", { replace: true });
      } else {
        const {
          payTo,
          // account,
          date,
          taxes = 0,
          details = [],
          documentNumber,
          attachments = [],
          description,
          subTotal = 0,
          notes,
          discount = 0,
          category,
          status,
          paymentMethod,
          tags = [],
          archived = false,
          shared = false,
          sharedBy = null,
          deleted = false,
          type,
          paymentVoucher,
          recurring,
        } = data || {};
        setClientProvider(payTo);
        setDate(date);
        setTaxes(taxes);
        setDetails(details);
        setDocumentNumber(documentNumber);
        setDescription(description);
        setActiveTab(type);
        setAttachments(attachments);
        setStatus(status);
        setArchived(archived);
        setDeleted(deleted);
        setReceived(shared);
        setPaymentMethod(paymentMethod);
        setTags(tags);
        setReceivedMetaData(sharedBy);
        setReceiptNumber(paymentVoucher);
        setRecurringSettings(() => recurring);
        setLoadedTransaction(data);

        if (!details?.length) {
          setManualSubTotal(subTotal);
        } else {
          setManualSubTotal("");
        }

        setNotes(notes);
        setDiscount(discount);
        setCategory(category);

        setIsLoadingExistingTransaction(false);
      }
    }
  };

  const isLoadingSomething = useMemo(
    () =>
      [
        !!isLoadingCurrentAccount,
        !!isLoadingAccounts,
        !!isLoadingExistingTransaction,
        !!isLoadingCustomizedSources,
        !!isLoadingAllTransactions,
        !!isLoadingCustomizedCategories,
      ].includes(true),
    [
      isLoadingCurrentAccount,
      isLoadingAccounts,
      isLoadingExistingTransaction,
      isLoadingCustomizedSources,
      isLoadingCustomizedCategories,
      isLoadingAllTransactions,
    ]
  );

  useEffect(() => {
    if (
      account &&
      transaction &&
      !isLoadingCurrentAccount &&
      !isLoadingAccounts &&
      !isLoadingAllTransactions &&
      !isLoadingCustomizedCategories &&
      !isLoadingCustomizedSources
    ) {
      setIsLoadingExistingTransaction(true);
      loadTransaction();
    }
  }, [
    transaction,
    account,
    isLoadingAccounts,
    isLoadingCurrentAccount,
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
    setIsLoadingExistingTransaction,
    isRefetchingAllTransactions,
  ]);

  useEffect(() => {
    if (!transaction && !isNewTransaction) {
      setIsNewTransaction(true);
      setDocumentNumber("");
      setClientProvider("");
      setDescription("");
      setCategory("");
      setAttachmentsToDelete([]);
      setUnsavedAttachments([]);
      setAttachments([]);
      setDetails([]);
      setManualSubTotal("");
      setDiscount("");
      setTaxes("");
      setNotes("");
      setReceiptNumber("");
      setRecurringSettings({
        settings: {
          frequency: "",
          customInterval: 0,
          createdAt: "",
          disabledAt: "",
        },
        enabled: false,
        nextPayment: "",
      });
      setLoadedTransaction(null);
    }
  }, [transaction]);

  const subtitleText = useMemo(() => {
    if (isNewTransaction) {
      return t("transaction_page.status.new");
    } else if (deleted) {
      return t("transaction_page.status.recycled");
    } else if (archived) {
      return t("transaction_page.status.archived");
    } else if (received) {
      return t("transaction_page.status.received");
    }

    return t("transaction_page.status.active");
  }, [isNewTransaction, deleted, archived, received]);

  const mobileTitle = useMemo(() => {
    if (isNewTransaction) {
      return t("transaction_page.status.mobile_new");
    } else if (deleted) {
      return t("transaction_page.status.mobile_recycled");
    } else if (archived) {
      return t("transaction_page.status.mobile_archived");
    } else if (received) {
      return t("transaction_page.status.mobile_received");
    }

    return t("transaction_page.status.mobile_active");
  }, [isNewTransaction, deleted, archived, received]);

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(attachmentsForSelector?.map?.((theFile: any) => {
        return {
          name: theFile.originalName,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() || "Desconocido",
          isSaved: true,
          thumbnailUrl: theFile?.thumbnailUrl,
          fileUrl: theFile?.fileUrl,
        };
      }) || []),
      ...(unsavedAttachments?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() || "Desconocido",
          isSaved: false,
          thumbnailUrl: theFile?.preview,
          fileUrl: theFile?.preview,
        };
      }) || []),
    ];
  }, [attachmentsForSelector, unsavedAttachments]);

  const attachmentSlides: any[] = useMemo(() => {
    return (
      unifiedAttachmentsList?.map?.((attachment: any) => {
        return {
          type: attachment?.type === "application/pdf" ? undefined : "image",
          src: attachment?.fileUrl,
        };
      }) || []
    );
  }, [unifiedAttachmentsList]);

  useEffect(() => {
    if (isNewTransaction) {
      setHasRequiredFieldsCompleted(
        !!(
          date &&
          paymentMethod &&
          status &&
          clientProvider &&
          category &&
          total &&
          (details?.length ? true : description)
        )
      );
    } else {
      if (!isLoadingSomething) {
        setHasRequiredFieldsCompleted(
          !!(
            date &&
            paymentMethod &&
            status &&
            clientProvider &&
            category &&
            total &&
            (details?.length ? true : description)
          )
        );
      }
    }
  }, [
    date,
    paymentMethod,
    status,
    clientProvider,
    category,
    total,
    details,
    description,
    isNewTransaction,
    isLoadingExistingTransaction,
    isLoadingSomething,
  ]);

  const mobileMenuMarquee = useMemo(() => {
    let message = [];

    if (date) {
      message.push(date);
    }

    if (account && currentAccountData) {
      message.push(currentAccountData?.name);
    }

    if (activeTab === ETransactionType.INCOME) {
      message.push(t("transaction_types.income"));
    } else if (activeTab === ETransactionType.EXPENSE) {
      message.push(t("transaction_types.expense"));
    }

    message.push(
      formatCurrency({
        value: total,
        currency: currencySymbol,
      })
    );

    if (paymentMethod) {
      message.push(getPaymentMethodById(paymentMethod)?.name);
    }

    return message;
  }, [total, paymentMethod, currencySymbol, activeTab, date]);

  const menuOptions = useMemo(() => {
    return orderBy(
      [
        {
          text: t("common_menu_items.archive"),
          onClick: () => setShowArchiveTransactionsDialog(true),
          icon: <MdOutlineArchive size={ICON_SIZES.MD} />,
          hide: archived || isNewTransaction,
        },
        {
          text: t("common_menu_items.unarchive"),
          onClick: () => setShowUnarchiveTransactionsDialog(true),
          icon: <MdOutlineUnarchive size={ICON_SIZES.MD} />,
          hide: !archived || isNewTransaction,
        },
        {
          icon: <MdRestoreFromTrash size={ICON_SIZES.MD} />,
          onClick: () => setShowRestoreTransactionsDialog(true),
          text: t("common_menu_items.restore"),
          hide: !deleted || isNewTransaction,
        },
        {
          text: t("common_menu_items.duplicate"),
          onClick: () => setShowDuplicateTransactionDialog(true),
          icon: <MdOutlineContentCopy size={ICON_SIZES.MD} />,
          hide: isNewTransaction,
        },
        {
          text: t("common_menu_items.move_account"),
          onClick: () => setShowMoveTransactionsDialog(true),
          icon: <MdOutlineCreditCard size={ICON_SIZES.MD} />,
          hide: isNewTransaction,
        },
        {
          icon: <MdOutlineAutoDelete size={ICON_SIZES.MD} />,
          onClick: () => setShowRecycleTransactionsDialog(true),
          text: t("common_menu_items.recycle"),
          hide: deleted || isNewTransaction,
        },
        {
          icon: <MdDelete size={ICON_SIZES.MD} />,
          onClick: () => setShowDeleteTransactionsDialog(true),
          text: t("common_menu_items.delete"),
          kind: EButtonKind.DANGER,
          hide: isNewTransaction,
        },
        {
          icon: <MdConnectWithoutContact size={ICON_SIZES.MD} />,
          onClick: () => setShowShareTransactionsDialog(true),
          text: t("common_menu_items.send"),
          hide: isNewTransaction,
        },
        {
          text: t("common_menu_items.load_xml"),
          onClick: () => triggerFileSelect(),
          icon: <MdFileUpload size={ICON_SIZES.MD} />,
          hide: !isNewTransaction,
        },
        {
          hide: isNewTransaction,
          text: t("common_menu_items.recurrence"),
          onClick: () => setShowRecurringSettings(true),
          icon: <MdOutlineRepeat size={ICON_SIZES.MD} />,
        },
      ],
      ["text"],
      ["asc"]
    );
  }, [currentAccountData, isNewTransaction, deleted, archived]);

  const hasChanges = useMemo(() => {
    if (isNewTransaction) {
      return false;
    }

    return objectHasChanges({
      original: {
        date: loadedTransaction?.date,
        documentNumber: loadedTransaction?.documentNumber,
        clientProvider: loadedTransaction?.payTo,
        description: loadedTransaction?.description,
        category: loadedTransaction?.category,
        details: loadedTransaction?.details,
        notes: loadedTransaction?.notes,
        total: loadedTransaction?.total,
        taxes: loadedTransaction?.taxes,
        discount: loadedTransaction?.discount,
        paymentMethod: loadedTransaction?.paymentMethod,
        status: loadedTransaction?.status,
        receiptNumber: loadedTransaction?.paymentVoucher,
        account: loadedTransaction?.account,
        type: loadedTransaction?.type,
        attachments: loadedTransaction?.attachments,
      },
      edited: {
        date,
        documentNumber,
        clientProvider,
        description,
        category,
        details,
        notes,
        total,
        taxes,
        discount,
        paymentMethod,
        status,
        receiptNumber,
        account,
        type: activeTab,
        attachments: [...attachmentsForSelector, ...unsavedAttachments],
      },
    });
  }, [
    loadedTransaction,
    date,
    documentNumber,
    clientProvider,
    description,
    category,
    details,
    notes,
    total,
    taxes,
    discount,
    paymentMethod,
    status,
    receiptNumber,
    account,
    activeTab,
    isNewTransaction,
    attachmentsForSelector,
    unsavedAttachments,
  ]);

  const canSave = useMemo(() => {
    if (isSavingTransaction) return false;
    if (!hasRequiredFieldsCompleted) return false;

    if (isNewTransaction) {
      return true;
    }

    if (!isSavingTransaction && hasRequiredFieldsCompleted) {
      return hasChanges;
    }

    return false;
  }, [isSavingTransaction, hasRequiredFieldsCompleted, hasChanges]);

  const showSidebar = useMemo(() => {
    if (
      isLoadingSomething ||
      !mainFilter?.account ||
      !currentAccountData ||
      currentAccountData?.settings?.disabled
    ) {
      return false;
    }

    if (isMobile) {
      return false;
    }

    return true;
  }, [isLoadingSomething, mainFilter?.account, currentAccountData, isMobile]);

  const updateTransactions = async ({
    fieldsToUpdate,
  }: {
    fieldsToUpdate: object;
  }) => {
    const transactionIds = [loadedTransaction?.invoice];

    return await updateTransactionsText({
      transactionIds,
      fieldsToUpdate,
    });
  };

  const duplicateSelection = async (customFields: any) => {
    setIsDuplicatingTransactions(true);

    const transactionIds = [loadedTransaction?.invoice];

    const customizedFields = {
      ...(customFields?.date?.change
        ? {
            date: moment(customFields?.date?.value).format("yyyy-MM-DD"),
          }
        : {}),
      ...(customFields?.account?.change
        ? {
            account: customFields?.account?.value,
          }
        : {}),
      recurring: {
        settings: {},
        enabled: false,
        nextPayment: "",
      },
    };

    if (transactionIds?.length) {
      const results = await Promise.allSettled(
        transactionIds?.map?.(async (transactionId: string) => {
          return await cloneTransaction({
            transactionId,
            customizeFields: customizedFields,
            newCloneId: uuidv4(),
            includeAttachments: !!customFields?.includeAttachments,
          });
        })
      );

      const successfulResults = results.filter((r) => r.status === "fulfilled");

      if (successfulResults?.length === transactionIds?.length) {
        await clearCache();
        setIsDuplicatingTransactions(false);
        setShowDuplicateTransactionDialog(false);

        showNotification({
          message: "Transacción duplicada correctamente",
          kind: "success",
        });
      } else {
        showNotification({
          message: "Ocurrió un al duplicar la transacción",
          kind: "error",
        });
      }
    }
  };

  const moveSelection = async ({ newAccount }: { newAccount: string }) => {
    let hasError = false;

    setIsMovingTransactions(true);

    await updateTransactions({ fieldsToUpdate: { account: newAccount } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            kind: "error",
            message: `Error moviendo la transacción`,
          });
          setIsMovingTransactions(false);
        } else {
          await clearCache();
          navigateToNexOrPrevTransaction();

          showNotification({
            message: "Transacción movida correctamente",
            kind: "success",
          });
        }

        setIsMovingTransactions(false);
        setShowMoveTransactionsDialog(false);
      }
    );
  };

  const deleteSelection = async () => {
    let hasError = false;

    setIsDeletingTransactions(true);

    await deleteTransactions({
      transactionIds: [loadedTransaction?.invoice],
      sendToRecycle: false,
    }).then(async (data) => {
      if (hasError) {
        showNotification({
          kind: "error",
          message: `Error eliminando la transacción`,
        });
        setIsDeletingTransactions(false);
      } else {
        navigate(`/transactions/${mainFilter?.account}`, {
          replace: true,
        });
        await clearCache();

        showNotification({
          message: "Transacción eliminada permanentemente",
          kind: "success",
        });
      }

      setIsDeletingTransactions(false);
      setShowDeleteTransactionsDialog(false);
    });
  };

  const archiveSelection = async () => {
    setIsArchivingTransactions(true);
    let hasError = false;

    await updateTransactions({
      fieldsToUpdate: { archived: true, deleted: false },
    }).then(async (data) => {
      if (hasError) {
        showNotification({
          message: `Ocurrió un error archivando la transacción"
            }!`,
          kind: "error",
        });
        setIsArchivingTransactions(false);
      } else {
        await clearCache();
        navigateToNexOrPrevTransaction();

        showNotification({
          kind: "success",
          message: "Transacción archivada correctamente",
        });
      }

      setIsArchivingTransactions(false);
      setShowArchiveTransactionsDialog(false);
    });
  };

  const restoreSelection = async () => {
    let hasError = false;

    setIsRestoringTransactions(true);

    await updateTransactions({ fieldsToUpdate: { deleted: false } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            kind: "error",
            message: `Error restaurando la transacción`,
          });
          setIsRestoringTransactions(false);
        } else {
          await clearCache();

          showNotification({
            message: "Transacción restaurada",
            kind: "success",
          });
        }

        setIsRestoringTransactions(false);
        setShowRestoreTransactionsDialog(false);
      }
    );
  };

  const unarchiveSelection = async () => {
    setIsUnarchivingTransactions(true);
    let hasError = false;

    await updateTransactions({ fieldsToUpdate: { archived: false } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            message: `Ocurrió un error desarchivando la transacción"
              }!`,
            kind: "error",
          });
          setIsUnarchivingTransactions(false);
        } else {
          await clearCache();

          showNotification({
            kind: "success",
            message: "Transacción desarchivada correctamente",
          });
        }

        setIsUnarchivingTransactions(false);
        setShowUnarchiveTransactionsDialog(false);
      }
    );
  };

  const recycleSelection = async () => {
    let hasError = false;

    setIsRecyclingTransactions(true);

    await updateTransactions({ fieldsToUpdate: { deleted: true } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            kind: "error",
            message: `Error moviendo la transacción a la papelera`,
          });
          setIsRecyclingTransactions(false);
        } else {
          await clearCache();
          navigateToNexOrPrevTransaction();

          showNotification({
            message: "Transacción movida a la papelera",
            kind: "success",
          });
        }

        setIsRecyclingTransactions(false);
        setShowRecycleTransactionsDialog(false);
      }
    );
  };

  const showTransactionNavigation = useMemo(() => {
    if (isNewTransaction || archived || deleted) {
      return false;
    }

    return true;
  }, [isNewTransaction, archived, deleted]);

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          showSidebar ? (
            <>
              {!isMobile && (
                <CollapsibleBlock
                  title={t("transaction_page.collapsibles.account")}
                  uppercase
                  open={collapsiblePreferences.accountOpen}
                  onChange={(isOpen: boolean) =>
                    updateCollapsible({ accountOpen: isOpen })
                  }
                  badge={formatCurrency({
                    value: getAvailableMoney,
                    currency: currencySymbol,
                  })}
                >
                  <CurrentAccountCard />
                </CollapsibleBlock>
              )}

              <TransactionTotals
                onSubTotalChange={(subTotal) => setManualSubTotal(subTotal)}
                discount={discount}
                taxes={taxes}
                subTotal={subTotal}
                total={total}
                onTaxesChange={(taxes) => setTaxes(taxes)}
                onDiscountChange={(discount) => setDiscount(discount)}
                isDetailed={!!details.length}
              />

              <TransactionPayment
                method={paymentMethod}
                status={status}
                onMethodChange={(value: string) => {
                  setPaymentMethod(value);
                  updateAccountLocalStorage({ lastPaymentMethod: value });
                }}
                onStatusChange={(value: string) => setStatus(value)}
                receiptNumber={receiptNumber}
                onReceiptNumberChange={(value: string) =>
                  setReceiptNumber(value)
                }
              />

              <div
                className={clsx([
                  "flex",
                  "gap-2",
                  "flex-row",
                  "w-full",
                  "items-center",
                  "justify-between",
                ])}
              >
                <div
                  className={clsx([
                    "text-sm",
                    "items-center",
                    "flex-row",
                    "gap-0",
                    "flex",
                  ])}
                >
                  <div
                    className={clsx([
                      "flex",
                      "flex-col",
                      "flex-shrink-0",
                      "text-xs",
                    ])}
                  >
                    {hasRequiredFieldsCompleted ? (
                      <div>
                        {t("transaction_page.misc.save_as")}&nbsp;
                        <strong className={clsx(["uppercase", "font-medium"])}>
                          {activeTab === ETransactionType.INCOME
                            ? t("transaction_types.income")
                            : t("transaction_types.expense")}
                        </strong>
                      </div>
                    ) : (
                      <div>
                        {t("transaction_page.misc.fields")}&nbsp;
                        <strong className={clsx(["uppercase", "font-medium"])}>
                          {t("transaction_page.misc.incomplete")}
                        </strong>
                      </div>
                    )}
                  </div>
                </div>

                <ButtonGroup
                  align="right"
                  buttons={[
                    {
                      text: isNewTransaction
                        ? t("common_buttons.save")
                        : t("common_buttons.update"),
                      onClick: () => setShowSaveDialog(true),
                      icon: <MdCheck size={ICON_SIZES.MD} />,
                      kind: EButtonKind.SAVE,
                      disabled: !canSave,
                      isLoading: isSavingTransaction,
                    },
                  ]}
                />
              </div>
            </>
          ) : null
        }
        mobileMenuChildren={
          <motion.div
            className={clsx([
              "bg-blue-900 md:bg-blue-200",
              "dark:bg-gray-900 dark:md:bg-gray-900",
              "flex",
              "w-full",
            ])}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className={clsx(["flex", "w-full"])}
            >
              <MarqueeRow>
                <>
                  {mobileMenuMarquee?.map?.((message: string, key: number) => (
                    <div
                      key={key}
                      className={clsx(["inline-flex", "items-center", "gap-3"])}
                    >
                      <span>{message}</span>
                      <MdArrowRight
                        className="text-red-600 dark:text-purple-400"
                        size={ICON_SIZES.XS}
                      />
                    </div>
                  ))}
                </>
              </MarqueeRow>
            </motion.div>
          </motion.div>
        }
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {mainFilter?.account ? (
              <>
                {isLoadingSomething ? (
                  <LoadingWithTextPage />
                ) : (
                  <>
                    {currentAccountData &&
                    !currentAccountData?.settings?.disabled ? (
                      <>
                        <PageTitle
                          icon={<MdOutlineSwapVert size={ICON_SIZES.MD} />}
                          title={
                            isMobile ? mobileTitle : t("transaction_page.title")
                          }
                          titleLink={`/transactions/${mainFilter?.account}`}
                          subtitle={
                            isMobile ? (
                              <PageSubtitleAccount {...currentAccountData} />
                            ) : (
                              subtitleText
                            )
                          }
                          rightSide={
                            <>
                              <ButtonGroup
                                buttons={[
                                  {
                                    icon: <MdMoreHoriz size={ICON_SIZES.MD} />,
                                    tooltip: t("common_tooltips.more_options"),
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                    options: menuOptions,
                                  },
                                  {
                                    icon: (
                                      <MdCheck
                                        size={ICON_SIZES.MD}
                                        className="text-current"
                                      />
                                    ),
                                    tooltip: t("common_tooltips.update"),
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING_SAVE
                                      : clsx([
                                          "md:!text-lime-400",
                                          "dark:md:!text-lime-400",
                                        ]),
                                    onClick: () => setShowSaveDialog(true),
                                    hide: !hasChanges,
                                    kind: EButtonKind.NORMAL,
                                  },
                                  accountsGlobalMenu,
                                  generalOptionsDropdown,
                                ]}
                                gap={1}
                              />
                            </>
                          }
                        />
                        <TodayTotalsBar />

                        <SlideDownUpAnimation
                          show={
                            !!(isMobile && collapsiblePreferences.accountOpen)
                          }
                        >
                          <CurrentAccountCard />
                        </SlideDownUpAnimation>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="text/xml"
                          onChange={onFileChange}
                          className="hidden"
                        />

                        <motion.div
                          className={clsx([
                            "flex",
                            "flex-row",
                            "justify-between",
                            "items-center",
                            "md:items-end",
                          ])}
                          id="focused-section"
                          {...{
                            initial: {
                              opacity: 0.99,
                            },
                            animate: { opacity: 1 },
                            transition: { duration: 0.1 },
                          }}
                          onAnimationComplete={() => {
                            if (
                              !!(isMobile && collapsiblePreferences.accountOpen)
                            ) {
                              setCanScroll(true);
                            }
                          }}
                        >
                          <LinksList
                            links={[
                              {
                                text: t("transaction_page.navigation.income"),
                                onClick: () =>
                                  setActiveTab(ETransactionType.INCOME),
                                active: activeTab === ETransactionType.INCOME,
                                icon: (
                                  <MdArrowDownward size={ICON_SIZES.MD_LG} />
                                ),
                                isLoading:
                                  !!isLoadingSomething &&
                                  activeTab === ETransactionType.INCOME,
                              },
                              {
                                text: t("transaction_page.navigation.expense"),
                                onClick: () =>
                                  setActiveTab(ETransactionType.EXPENSE),
                                active: activeTab === ETransactionType.EXPENSE,
                                icon: <MdArrowUpward size={ICON_SIZES.MD_LG} />,
                                isLoading:
                                  !!isLoadingSomething &&
                                  activeTab === ETransactionType.EXPENSE,
                              },
                              {
                                text: TRANSACTION_TYPES.LOAN.NAME,
                                onClick: () =>
                                  setActiveTab(ETransactionType.LOAN),
                                active: activeTab === ETransactionType.LOAN,
                                icon: (
                                  <MdOutlineAccountBalance
                                    size={ICON_SIZES.MD_LG}
                                  />
                                ),
                                isLoading:
                                  !!isLoadingSomething &&
                                  activeTab === ETransactionType.LOAN,
                                hide: true,
                              },
                            ]}
                          />

                          <AnimatePresence mode="wait">
                            {showTransactionNavigation && (
                              <motion.div
                                initial={{ opacity: 0, x: 100 }}
                                animate={{
                                  opacity: 1,
                                  x: 0,
                                }}
                                exit={{ opacity: 0, x: 100 }}
                                transition={{
                                  duration: 0.3,
                                  ease: "easeOut",
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 17,
                                }}
                              >
                                <TransactionsNavigation
                                  onChange={({ prev, next }) => {
                                    setNextTransactionInNavigation(
                                      next?.transaction || ""
                                    );
                                    setPrevTransactionInNavigation(
                                      prev?.transaction || ""
                                    );
                                  }}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>

                        <TransactionClientProviderInfo
                          clientProvider={clientProvider}
                          transactionType={activeTab}
                          onNonExistingClick={() => {}}
                        />

                        {/* <SlideDownUpAnimation show={hasChanges}>
                          <InfoBlock
                            bg={"dark:bg-red-500 bg-red-200"}
                            displayMobileIcon
                            title="Psst!"
                            message="Tienes cambios sin guardar."
                            icon={
                              <MdOutlineSaveAs
                                size={
                                  isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG
                                }
                                className="text-red-500 dark:text-red-100"
                              />
                            }
                          />
                        </SlideDownUpAnimation> */}

                        <SlideDownUpAnimation show={deleted}>
                          <InfoBlock
                            displayMobileIcon
                            title="Transacción en la papelera"
                            message="Esta transacción está en la papelera y no se toma en cuenta en tu balance."
                            icon={<MdOutlineAutoDelete size={ICON_SIZES.MD} />}
                          />
                        </SlideDownUpAnimation>

                        <SlideDownUpAnimation show={archived}>
                          <InfoBlock
                            displayMobileIcon
                            title="Transacción archivada"
                            message="El monto de esta transacción no se toma en cuenta en tu balance porque está archivada."
                            icon={<MdOutlineArchive size={ICON_SIZES.MD} />}
                          />
                        </SlideDownUpAnimation>

                        {!isLoadingExistingTransaction && (
                          <div
                            className={clsx([
                              "w-full",
                              "flex",
                              "flex-col",
                              "gap-1",
                              {
                                // hidden: hasRequiredFieldsCompleted,
                              },
                            ])}
                          >
                            <SlideDownUpAnimation
                              show={!hasRequiredFieldsCompleted}
                            >
                              <FieldsValidation
                                message={t(
                                  "transaction_page.validation.modal_message"
                                )}
                                showButton
                                showFields={false}
                                fieldsToValidate={[
                                  {
                                    name: t(
                                      "transaction_page.validation.fields.date"
                                    ),
                                    completed: !!date,
                                  },
                                  {
                                    name: t(
                                      "transaction_page.validation.fields.payment_method"
                                    ),
                                    completed: !!paymentMethod,
                                  },
                                  {
                                    name: t(
                                      "transaction_page.validation.fields.status"
                                    ),
                                    completed: !!status,
                                  },

                                  {
                                    name:
                                      activeTab === ETransactionType.EXPENSE
                                        ? t(
                                            "transaction_page.validation.fields.destination"
                                          )
                                        : t(
                                            "transaction_page.validation.fields.origin"
                                          ),
                                    completed: !!clientProvider,
                                  },
                                  ...(details?.length
                                    ? []
                                    : [
                                        {
                                          name: t(
                                            "transaction_page.validation.fields.description"
                                          ),
                                          completed: !!description,
                                        },
                                      ]),
                                  {
                                    name: t(
                                      "transaction_page.validation.fields.category"
                                    ),
                                    completed: !!category,
                                  },
                                  {
                                    name: t(
                                      "transaction_page.validation.fields.total"
                                    ),
                                    completed: !!total,
                                  },
                                ]}
                                onChange={setHasRequiredFieldsCompleted}
                              />
                            </SlideDownUpAnimation>

                            <SlideDownUpAnimation
                              show={!!recurringSettings?.enabled}
                            >
                              <RecurringTransactionInfo
                                settings={{ ...recurringSettings }}
                                buttons={[
                                  {
                                    tooltip: t("common_tooltips.register"),
                                    // text: isMobile ? "" : "Registrar",
                                    icon: (
                                      <MdOutlineCreditScore
                                        size={ICON_SIZES.MD}
                                      />
                                    ),
                                    kind: EButtonKind.SAVE,
                                    onClick: () =>
                                      setShowRecurringPaymentModal(true),
                                  },
                                ]}
                              />
                            </SlideDownUpAnimation>

                            {!!(
                              loadedTransaction?.origin && !isNewTransaction
                            ) && (
                              <RecurringTransactionOrigin
                                transaction={loadedTransaction}
                                buttons={[
                                  {
                                    icon: (
                                      <MdOutlineFolder size={ICON_SIZES.MD} />
                                    ),
                                    onClick: () =>
                                      navigate(
                                        `/transaction/${loadedTransaction?.account}/${loadedTransaction?.origin?.from}`
                                      ),
                                    tooltip: "Ver transacción",
                                  },
                                ]}
                              />
                            )}
                          </div>
                        )}

                        <div
                          className={clsx([
                            "w-full",
                            "flex",
                            "flex-col",
                            "gap-4",
                            "relative",
                            draggingStyles,
                          ])}
                          onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onDrop={onDrop}
                        >
                          <TransactionHeader
                            clientProvider={clientProvider}
                            onClientProviderChange={setClientProvider}
                            date={date}
                            onDateChange={setDate}
                            documentNumber={documentNumber}
                            onDocumentNumberChange={setDocumentNumber}
                            transactionType={activeTab}
                            category={category}
                            onCategoryChange={setCategory}
                            description={description}
                            onDescriptionChange={setDescription}
                            details={details}
                            onMethodChange={setPaymentMethod}
                            isNewTransaction={isNewTransaction}
                          />

                          <TransactionAttachments
                            key={attachmentsKey}
                            attachments={attachmentsForSelector}
                            onChange={({ unsaved, deleted }) => {
                              setUnsavedAttachments(unsaved);
                              setAttachmentsToDelete([
                                ...attachmentsToDelete,
                                ...deleted,
                              ]);
                            }}
                            onClick={({
                              saved,
                              index,
                            }: {
                              saved: boolean;
                              index: number;
                            }) => {
                              setClickedAttachmentIndex(index);
                              setAttachmentsGalleryIsOpen(true);
                            }}
                          />

                          <div
                            className={clsx([
                              "flex",
                              "w-full",
                              "relative",
                              "gap-4",
                              "md:flex-col-reverse",
                              "flex-col",
                            ])}
                          >
                            <TransactionNotes
                              notes={notes}
                              onNotesChange={setNotes}
                            />

                            <TransactionDetails
                              details={details || []}
                              onDetailsChange={(details: any) => {
                                setDetails(details);
                              }}
                            />

                            {isMobile && (
                              <>
                                {!isMobile && (
                                  <CollapsibleBlock
                                    title={t(
                                      "transaction_page.collapsibles.account"
                                    )}
                                    uppercase
                                    open={collapsiblePreferences.accountOpen}
                                    onChange={(isOpen: boolean) =>
                                      updateCollapsible({ accountOpen: isOpen })
                                    }
                                    badge={formatCurrency({
                                      value: getAvailableMoney,
                                      currency: currencySymbol,
                                    })}
                                  >
                                    <CurrentAccountCard />
                                  </CollapsibleBlock>
                                )}

                                <TransactionTotals
                                  onSubTotalChange={(subTotal) =>
                                    setManualSubTotal(subTotal)
                                  }
                                  discount={discount}
                                  taxes={taxes}
                                  subTotal={subTotal}
                                  total={total}
                                  onTaxesChange={(taxes) => setTaxes(taxes)}
                                  onDiscountChange={(discount) =>
                                    setDiscount(discount)
                                  }
                                  isDetailed={!!details.length}
                                />

                                <TransactionPayment
                                  method={paymentMethod}
                                  status={status}
                                  onMethodChange={(value: string) => {
                                    setPaymentMethod(value);
                                    updateAccountLocalStorage({
                                      lastPaymentMethod: value,
                                    });
                                  }}
                                  onStatusChange={(value: string) =>
                                    setStatus(value)
                                  }
                                  receiptNumber={receiptNumber}
                                  onReceiptNumberChange={(value: string) =>
                                    setReceiptNumber(value)
                                  }
                                />

                                <div
                                  className={clsx([
                                    "flex",
                                    "gap-2",
                                    "flex-row",
                                    "w-full",
                                    "items-center",
                                    "justify-between",
                                  ])}
                                >
                                  <div
                                    className={clsx([
                                      "text-sm",
                                      "items-center",
                                      "flex-row",
                                      "gap-0",
                                      "flex",
                                    ])}
                                  >
                                    <div
                                      className={clsx([
                                        "flex",
                                        "flex-col",
                                        "flex-shrink-0",
                                        "text-xs",
                                      ])}
                                    >
                                      {hasRequiredFieldsCompleted ? (
                                        <div>
                                          {t("transaction_page.misc.save_as")}
                                          &nbsp;
                                          <strong
                                            className={clsx([
                                              "uppercase",
                                              "font-medium",
                                            ])}
                                          >
                                            {activeTab ===
                                            ETransactionType.INCOME
                                              ? t("transaction_types.income")
                                              : t("transaction_types.expense")}
                                          </strong>
                                        </div>
                                      ) : (
                                        <div>
                                          {t("transaction_page.misc.fields")}
                                          &nbsp;
                                          <strong
                                            className={clsx([
                                              "uppercase",
                                              "font-medium",
                                            ])}
                                          >
                                            {t(
                                              "transaction_page.misc.incomplete"
                                            )}
                                          </strong>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <ButtonGroup
                                    align="right"
                                    buttons={[
                                      {
                                        text: isNewTransaction
                                          ? t("common_buttons.save")
                                          : t("common_buttons.update"),
                                        onClick: () => setShowSaveDialog(true),
                                        icon: <MdCheck size={ICON_SIZES.MD} />,
                                        kind: EButtonKind.SAVE,
                                        disabled: !canSave,
                                        isLoading: isSavingTransaction,
                                      },
                                    ]}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <ScrollToSection
                          targetId="focused-section"
                          enabled={canScroll}
                        />
                      </>
                    ) : (
                      <AccountNotFound
                        linkTo="transaction"
                        exists={!!currentAccountData}
                        disabled={!!currentAccountData?.settings?.disabled}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <AccountNotFound linkTo="transaction" />
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>

      {showSaveDialog && (
        <ConfirmDialog
          description={
            isNewTransaction
              ? "¿Deseas agregar la nueva transacción?"
              : t("common_dialogs.save_changes")
          }
          title={
            isNewTransaction
              ? t("common_dialog_titles.save")
              : t("common_dialog_titles.update")
          }
          confirmKind={EButtonKind.SAVE}
          onClose={() => {
            setShowSaveDialog(false);
          }}
          isLoading={isSavingTransaction}
          onConfirm={async () => {
            if (isNewTransaction) {
              await saveTransaction();
            } else {
              await updateTransaction();
            }
          }}
        />
      )}

      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={attachmentSlides}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          thumbnailsRef={thumbnailsRef}
          unifiedAttachmentsList={unifiedAttachmentsList}
          onClose={() => setAttachmentsGalleryIsOpen(false)}
        />
      )}

      {showRecurringSettings && (
        <RecurringTransactionSettings
          onClose={() => setShowRecurringSettings(false)}
          transaction={transaction || ""}
          currentSettings={recurringSettings}
          onSave={async () => {
            loadTransaction();

            setShowRecurringSettings(false);
          }}
        />
      )}

      {showRecurringPaymentModal && (
        <RecurringTransactionPayment
          transaction={loadedTransaction}
          onClose={() => setShowRecurringPaymentModal(false)}
        />
      )}

      {showArchiveTransactionsDialog && (
        <ConfirmDialog
          description={t("¿Deseas archivar esta transacción?")}
          onClose={() => setShowArchiveTransactionsDialog(false)}
          onConfirm={async () => await archiveSelection()}
          title={t("dialogs.archive_transactions.title")}
          isLoading={isArchivingTransactions}
        />
      )}

      {showUnarchiveTransactionsDialog && (
        <ConfirmDialog
          description={t("¿Deseas desarchivar esta transacción?")}
          onClose={() => setShowUnarchiveTransactionsDialog(false)}
          onConfirm={async () => await unarchiveSelection()}
          title={t("dialogs.unarchive_transactions.title")}
          isLoading={isUnarchivingTransactions}
        />
      )}

      {showRecycleTransactionsDialog && (
        <ConfirmDialog
          description={t("¿Deseas mover esta transacción a la papelera?")}
          onClose={() => setShowRecycleTransactionsDialog(false)}
          onConfirm={async () => await recycleSelection()}
          title={t("dialogs.recycle_transactions.title")}
          confirmKind={EButtonKind.DANGER}
          isLoading={isRecyclingTransactions}
        />
      )}

      {showRestoreTransactionsDialog && (
        <ConfirmDialog
          description={t("¿Deseas restaurar esta transacción?")}
          onClose={() => setShowRestoreTransactionsDialog(false)}
          onConfirm={async () => await restoreSelection()}
          title={t("dialogs.restore_transactions.title")}
          confirmKind={EButtonKind.SAVE}
          isLoading={isRestoringTransactions}
        />
      )}

      {showShareTransactionsDialog && (
        <ShareTransactionModal
          onClose={() => setShowShareTransactionsDialog(false)}
          onSend={() => {}}
        />
      )}

      {showDuplicateTransactionDialog && (
        <DuplicateTransactionsDialog
          onClose={() => setShowDuplicateTransactionDialog(false)}
          isLoading={isDuplicatingTransactions}
          onConfirm={async (values: any) => {
            await duplicateSelection(values);
          }}
          count={1}
        />
      )}

      {showMoveTransactionsDialog && (
        <SelectDialog
          description={t("dialogs.move_transactions.move_question", {
            count: 1,
          })}
          doubleConfirmRequired
          note={
            allUserAccounts?.length === 1
              ? t("dialogs.move_transactions.move_need_another_account")
              : t("dialogs.move_transactions.move_question_note", {
                  count: 1,
                })
          }
          onClose={() => {
            setShowMoveTransactionsDialog(false);
          }}
          onConfirm={async (to: string) => {
            await moveSelection({ newAccount: to });
          }}
          isLoading={isMovingTransactions}
          title={t("dialogs.move_transactions.title")}
          label="Cuenta destino"
          confirmKind={EButtonKind.SAVE}
          options={
            transformToSelectOptions({
              data: allUserAccounts?.filter?.(
                (account: any) => account?.account !== mainFilter?.account
              ),
              labelField: "name",
              valueField: "account",
            }) as Array<ICustomSelectOption>
          }
        />
      )}

      {showDeleteTransactionsDialog && (
        <ConfirmDialog
          description={t("¿Deseas eliminar la transacción?")}
          note={t("dialogs.delete_transactions.delete_question_note", {
            count: 1,
          })}
          onClose={() => setShowDeleteTransactionsDialog(false)}
          doubleConfirmRequired
          onConfirm={async () => await deleteSelection()}
          title={t("dialogs.delete_transactions.title")}
          confirmKind={EButtonKind.DANGER}
          isLoading={isDeletingTransactions}
        />
      )}
    </>
  );
};

export default NewTransactionPage;
